// In-memory fixtures behind DashboardApi, so every screen renders and every
// action works before apps/api's dashboard routes exist. Shapes match
// docs/api/dashboard-contract.md field for field (./types.ts). Sample people
// and numbers are invented for the demo; no real person or NIK is used.
//
// Times are relative to the moment the store is created, so "today" always
// has arrivals and the active codes are always still active.
import { ApiError, type CsvExport, type DashboardApi } from "./api";
import { fixtureCheck, fixtureDemografi, fixtureDetail, fixtureListData } from "./demografi-fixture";
import type {
  Connection,
  Ok,
  PairingCode,
  PairingCodeFilter,
  Sender,
  SenderFilter,
  SenderPii,
  Submission,
  SubmissionFilters,
  SubmissionPage,
  SubmissionPii,
} from "./types";

/** Accepted by the fixture validateToken (any 32+ characters is). Used by the
 *  ?demo=1 switch in ./session.ts so the signed-in screens can be audited. */
export const DEMO_TOKEN = "fixture-demo-token-not-a-real-secret-0000";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "+6281234567789" to "+62812****789": country code plus first 3, last 3. */
export function maskPhone(phone: string): string {
  const rest = phone.slice(3);
  return `+62${rest.slice(0, 3)}****${rest.slice(-3)}`;
}

/** "3201234567890123" to "3201********0123": first 4, last 4. */
export function maskNik(nik: string): string {
  return `${nik.slice(0, 4)}********${nik.slice(-4)}`;
}

interface SenderSeed {
  id: string;
  phone: string;
  label: string;
  pairedDaysAgo: number;
  revokedDaysAgo: number | null;
}

const SENDER_SEEDS: SenderSeed[] = [
  { id: "snd-01", phone: "+6281234567789", label: "Posko Kelurahan Cibinong", pairedDaysAgo: 21, revokedDaysAgo: null },
  { id: "snd-02", phone: "+6285711224356", label: "Relawan Bekasi Timur", pairedDaysAgo: 14, revokedDaysAgo: null },
  { id: "snd-03", phone: "+6281398760412", label: "Kantor Desa Sukamaju", pairedDaysAgo: 10, revokedDaysAgo: null },
  { id: "snd-04", phone: "+6287822340917", label: "Petugas RW 05 Depok", pairedDaysAgo: 9, revokedDaysAgo: null },
  { id: "snd-05", phone: "+6282145561230", label: "Posko Ciputat (lama)", pairedDaysAgo: 30, revokedDaysAgo: 3 },
];

/** [name, nik, sender id or null, minutes ago] */
const SUBMISSION_SEEDS: [string, string, string | null, number][] = [
  ["Siti Rahmawati", "3201014508920003", "snd-01", 7],
  ["Budi Santoso", "3275031203880011", "snd-02", 26],
  ["Dewi Lestari", "3201026107950002", "snd-01", 48],
  ["Agus Setiawan", "3276052209840007", "snd-04", 95],
  ["Nur Aisyah", "3201035303990004", "snd-03", 140],
  ["Muhammad Iqbal Ramadhan", "3275041711010009", "snd-02", 205],
  ["Fitri Handayani", "3201015902930006", "snd-01", 24 * 60 + 35],
  ["Rizky Pratama", "3276011406970012", "snd-04", 24 * 60 + 110],
  ["Ayu Wulandari", "3201034810960001", "snd-03", 24 * 60 + 260],
  ["Hendra Gunawan", "3275022502860013", null, 2 * 24 * 60 + 15],
  ["Lina Kartika", "3674054404910005", "snd-05", 4 * 24 * 60 + 40],
  ["Yusuf Hidayat", "3674050808890008", "snd-05", 5 * 24 * 60 + 120],
  ["Rina Marlina", "3201016612940010", "snd-01", 6 * 24 * 60 + 300],
  ["Dedi Kurniawan", "3276030101820014", "snd-02", 8 * 24 * 60 + 75],
];

interface CodeRecord {
  id: string;
  code: string;
  label: string;
  createdAt: number;
  /** null: never expires (default since the API stopped taking a TTL from the dashboard). */
  expiresAt: number | null;
  usedAt: number | null;
  usedByPhone: string | null;
}

interface SenderRecord extends SenderSeed {
  pairedAt: number;
  revokedAt: number | null;
}

/** A submission as stored: the full phone and NIK stay here and leave only
 *  through revealSubmission, as the live API does (SEC-05). */
interface SubmissionRecord extends Submission {
  phone: string;
  nik: string;
}

/** The list shape: the record without its full values. */
function publicSubmission({ phone: _phone, nik: _nik, ...rest }: SubmissionRecord): Submission {
  return rest;
}

interface Store {
  senders: SenderRecord[];
  submissions: SubmissionRecord[];
  codes: CodeRecord[];
}

function seed(now: number): Store {
  const senders: SenderRecord[] = SENDER_SEEDS.map((s) => ({
    ...s,
    pairedAt: now - s.pairedDaysAgo * DAY,
    revokedAt: s.revokedDaysAgo === null ? null : now - s.revokedDaysAgo * DAY,
  }));
  const submissions: SubmissionRecord[] = SUBMISSION_SEEDS.map(([name, nik, senderId, minutesAgo], i) => {
    const sender = senders.find((s) => s.id === senderId);
    const phone = sender?.phone ?? "+6281511447063";
    return {
      id: `sub-${String(i + 1).padStart(3, "0")}`,
      phone,
      phoneMasked: maskPhone(phone),
      name,
      nik,
      nikMasked: maskNik(nik),
      receivedAt: new Date(now - minutesAgo * MINUTE).toISOString(),
      senderLabel: sender?.label ?? null,
    };
  });
  const codes: CodeRecord[] = [
    { id: "code-04", code: "482913", label: "Relawan Ciputat", createdAt: now - 20 * MINUTE, expiresAt: null, usedAt: null, usedByPhone: null },
    { id: "code-03", code: "075261", label: "Posko Kelurahan Pabuaran", createdAt: now - 2 * HOUR, expiresAt: null, usedAt: null, usedByPhone: null },
    { id: "code-02", code: "316540", label: "Kantor Desa Cileungsi", createdAt: now - 26 * HOUR, expiresAt: now - 25 * HOUR, usedAt: null, usedByPhone: null },
    {
      id: "code-01",
      code: "908172",
      label: "Petugas RW 05 Depok",
      createdAt: now - 9 * DAY - HOUR,
      expiresAt: now - 9 * DAY + 3 * HOUR,
      usedAt: now - 9 * DAY,
      usedByPhone: "+6287822340917",
    },
  ];
  return { senders, submissions, codes };
}

function codeStatus(c: CodeRecord, now: number): PairingCode["status"] {
  if (c.usedAt !== null) return "used";
  return c.expiresAt === null || c.expiresAt > now ? "active" : "expired";
}

function toPairingCode(c: CodeRecord, now: number): PairingCode {
  return {
    id: c.id,
    code: c.code,
    label: c.label,
    createdAt: new Date(c.createdAt).toISOString(),
    expiresAt: c.expiresAt === null ? null : new Date(c.expiresAt).toISOString(),
    usedAt: c.usedAt === null ? null : new Date(c.usedAt).toISOString(),
    usedByPhoneMasked: c.usedByPhone === null ? null : maskPhone(c.usedByPhone),
    status: codeStatus(c, now),
  };
}

function digitsOf(s: string): string {
  return s.replace(/\D/g, "");
}

/** Local calendar date of an instant, "2026-10-14". */
function localDate(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function matches(s: SubmissionRecord, f: SubmissionFilters): boolean {
  const q = f.q?.trim();
  if (q) {
    const byName = s.name.toLowerCase().includes(q.toLowerCase());
    const byNik = /^\d+$/.test(q) && s.nik.startsWith(q);
    if (!byName && !byNik) return false;
  }
  const phone = f.phone ? digitsOf(f.phone).replace(/^0/, "62") : "";
  if (phone && !digitsOf(s.phone).includes(phone)) return false;
  const day = localDate(Date.parse(s.receivedAt));
  if (f.from && day < f.from) return false;
  if (f.to && day > f.to) return false;
  return true;
}

/** Contract CSV cell: always quoted; a leading =, +, -, @, tab, or CR gets a single quote. */
function csvCell(value: string): string {
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

function randomCode(taken: Set<string>): string {
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    const code = String((buf[0] ?? 0) % 1_000_000).padStart(6, "0");
    if (!taken.has(code)) return code;
  }
}

/** The fixture DashboardApi. One store per instance, so tests stay isolated. */
export function createFixtureApi(nowFn: () => number = Date.now): DashboardApi {
  const store = seed(nowFn());
  const ok: Ok = { ok: true };
  // The bot number starts unlinked; a pairing request answers with a fake
  // 8-character code, as the Baileys transport does.
  let connection: Connection = {
    transport: "baileys",
    status: "disconnected",
    phone: null,
    pairingCode: null,
    pairingRequestedAt: null,
    lastError: null,
    updatedAt: new Date(nowFn()).toISOString(),
    botPhone: null,
    botPhoneSource: null,
  };

  const filtered = (f: SubmissionFilters) =>
    store.submissions
      .filter((s) => matches(s, f))
      .sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));

  return {
    async demografi(scope) {
      return fixtureDemografi(scope, new Date(nowFn()));
    },

    async checkNik(nik) {
      return fixtureCheck(nik);
    },

    async listData(scope, page, pageSize) {
      return fixtureListData(scope, page, pageSize, new Date(nowFn()));
    },

    async submissionDetail(id) {
      // Kiriman's own fixture rows are not demo people: show the canned answer for them.
      const detail = fixtureDetail(id, new Date(nowFn()));
      if (detail) return detail;
      const row = store.submissions.find((s) => s.id === id);
      if (!row) throw new ApiError(404, "not_found");
      const ekstraksi = fixtureCheck(row.nik);
      return {
        kiriman: { id: row.id, nama: row.name, nik: row.nik, nomor: row.phone, pengirim: row.senderLabel, diterima: row.receivedAt },
        ekstraksi,
        terprofil: true,
        dinilaiPada: null,
      };
    },

    async validateToken(token: string) {
      return token.trim().length >= 32;
    },

    async overview() {
      const now = nowFn();
      const today = localDate(now);
      return {
        pairedSenders: store.senders.filter((s) => s.revokedAt === null).length,
        submissionsToday: store.submissions.filter((s) => localDate(Date.parse(s.receivedAt)) === today).length,
        submissionsTotal: store.submissions.length,
        activeCodes: store.codes.filter((c) => codeStatus(c, now) === "active").length,
        serverTime: new Date(now).toISOString(),
      };
    },

    async listSubmissions(f: SubmissionFilters): Promise<SubmissionPage> {
      const page = Math.max(1, f.page ?? 1);
      const pageSize = Math.min(200, Math.max(1, f.pageSize ?? 50));
      const all = filtered(f);
      return { items: all.slice((page - 1) * pageSize, page * pageSize).map(publicSubmission), page, pageSize, total: all.length };
    },

    async revealSubmission(id: string): Promise<SubmissionPii> {
      const s = store.submissions.find((x) => x.id === id);
      if (!s) throw new ApiError(404, "not_found");
      return { id: s.id, phone: s.phone, nik: s.nik };
    },

    async exportCsv(f: SubmissionFilters): Promise<CsvExport> {
      const rows = filtered(f).map((s) =>
        [s.receivedAt, s.phone, s.name, s.nik, s.senderLabel ?? ""].map(csvCell).join(","),
      );
      const body = ["received_at,phone,name,nik,sender_label", ...rows].join("\r\n");
      const stamp = localDate(nowFn()).replace(/-/g, "");
      return {
        filename: `submissions-${stamp}.csv`,
        blob: new Blob([String.fromCharCode(0xfeff), body, "\r\n"], { type: "text/csv;charset=utf-8" }),
      };
    },

    async listCodes(status: PairingCodeFilter) {
      const now = nowFn();
      const items = store.codes
        .map((c) => toPairingCode(c, now))
        .filter((c) => status === "all" || c.status === status)
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
      return { items };
    },

    async createCode(label: string) {
      const clean = label.trim();
      if (clean.length < 1 || clean.length > 64) throw new ApiError(400, "label must be 1 to 64 characters");
      const now = nowFn();
      const taken = new Set(store.codes.filter((c) => codeStatus(c, now) === "active").map((c) => c.code));
      const record: CodeRecord = {
        id: `code-${crypto.randomUUID()}`,
        code: randomCode(taken),
        label: clean,
        createdAt: now,
        expiresAt: null,
        usedAt: null,
        usedByPhone: null,
      };
      store.codes.unshift(record);
      return toPairingCode(record, now);
    },

    async deleteSubmission(id: string) {
      const index = store.submissions.findIndex((x) => x.id === id);
      if (index < 0) throw new ApiError(404, "not_found");
      store.submissions.splice(index, 1);
      return ok;
    },

    async deleteCode(id: string) {
      const c = store.codes.find((x) => x.id === id);
      if (!c) throw new ApiError(404, "not found");
      if (c.usedAt !== null) throw new ApiError(409, "already used");
      c.expiresAt = c.expiresAt === null ? nowFn() : Math.min(c.expiresAt, nowFn());
      return ok;
    },

    async listSenders(status: SenderFilter) {
      const items: Sender[] = store.senders
        .filter((s) => status === "all" || (status === "active" ? s.revokedAt === null : s.revokedAt !== null))
        .map((s) => ({
          id: s.id,
          phoneMasked: maskPhone(s.phone),
          label: s.label,
          pairedAt: new Date(s.pairedAt).toISOString(),
          revokedAt: s.revokedAt === null ? null : new Date(s.revokedAt).toISOString(),
          submissionCount: store.submissions.filter((x) => x.phone === s.phone).length,
        }))
        .sort((a, b) => Date.parse(b.pairedAt) - Date.parse(a.pairedAt));
      return { items };
    },

    async revealSender(id: string): Promise<SenderPii> {
      const s = store.senders.find((x) => x.id === id);
      if (!s) throw new ApiError(404, "not_found");
      return { id: s.id, phone: s.phone };
    },

    async revokeSender(id: string) {
      const s = store.senders.find((x) => x.id === id);
      if (!s) throw new ApiError(404, "not found");
      s.revokedAt = nowFn();
      return ok;
    },

    async restoreSender(id: string) {
      const s = store.senders.find((x) => x.id === id);
      if (!s) throw new ApiError(404, "not found");
      s.revokedAt = null;
      return ok;
    },

    async connection() {
      return connection;
    },

    async requestPairingCode(phone: string) {
      if (!/^\+?[0-9][0-9 -]*$/.test(phone.trim())) {
        throw new ApiError(400, "invalid_request", [{ path: "phone", message: "must be a phone number" }]);
      }
      const raw = digitsOf(phone);
      const digits = raw.startsWith("08") ? `62${raw.slice(1)}` : raw;
      if (digits.length < 10 || digits.length > 15) {
        throw new ApiError(400, "invalid_request", [{ path: "phone", message: "must be 10 to 15 digits" }]);
      }
      if (connection.botPhone && connection.botPhone !== `+${digits}`) throw new ApiError(400, "not_bot_phone");
      if (connection.status === "open") throw new ApiError(409, "already_linked");
      const at = new Date(nowFn()).toISOString();
      connection = {
        ...connection,
        status: "pairing",
        phone: `+${digits}`,
        botPhone: `+${digits}`,
        botPhoneSource: "linked",
        pairingCode: "K7QF2M9X",
        pairingRequestedAt: at,
        lastError: null,
        updatedAt: at,
      };
      return connection;
    },

    async logoutConnection() {
      connection = {
        ...connection,
        status: "logged_out",
        phone: null,
        botPhone: null,
        botPhoneSource: null,
        pairingCode: null,
        pairingRequestedAt: null,
        updatedAt: new Date(nowFn()).toISOString(),
      };
      return connection;
    },
  };
}
